import type {
  CardDisplayType,
  DatabaseId,
  Dataset,
  VisualizationSettings,
} from "metabase-types/api";

export type AdhocTabId = string;
export type AdhocResultId = string;

/**
 * Bump whenever the persisted shape changes incompatibly. The version appears
 * both in the storage key (free isolation) and in the payload (so a real
 * migration can be written later).
 */
export const ADHOC_STORAGE_VERSION = 1;

/* -------------------------------------------------------------------------
 * Persisted state (localStorage)
 * ---------------------------------------------------------------------- */

/**
 * A single query tab, as persisted. Query *results* are never persisted.
 *
 * Only the data source and the SQL text are stored. The MBQL query itself is
 * rebuilt through Lib on load: it is an internal format that has changed shape
 * between Metabase versions, and Lib is also what derives the template tags for
 * `{{snippet: …}}` and `{{#123-a-model}}` references.
 */
export type PersistedAdhocTab = {
  id: AdhocTabId;
  name: string;
  databaseId: DatabaseId | null;
  sql: string;
  /** Height of the SQL editor pane, in pixels, if the user resized it. */
  editorHeight?: number;
};

export type PersistedWorkbench = {
  version: typeof ADHOC_STORAGE_VERSION;
  tabs: PersistedAdhocTab[];
  activeTabId: AdhocTabId | null;
  /** Kept so default tab names stay unique across reloads. */
  nextTabIndex: number;
  /** Epoch millis of the last write; used to detect a newer write from another window. */
  savedAt: number;
};

/* -------------------------------------------------------------------------
 * Ephemeral state (in memory only)
 * ---------------------------------------------------------------------- */

export type AdhocResultStatus = "running" | "completed" | "error" | "cancelled";

/**
 * One execution of a tab's SQL. A result is a *snapshot*: it keeps the query as
 * it was executed, so editing the SQL afterwards never changes what an older
 * result displays or exports.
 */
export type AdhocResult = {
  id: AdhocResultId;
  /** Monotonic per tab, so closing "Result 2" leaves "Result 1" and "Result 3". */
  index: number;
  name: string;
  status: AdhocResultStatus;
  /** The data source and SQL as executed — may be a selected fragment. */
  databaseId: DatabaseId;
  sql: string;
  display: CardDisplayType;
  visualizationSettings: VisualizationSettings;
  startedAt: number;
  finishedAt: number | null;
  dataset: Dataset | null;
};

export type AdhocTabRuntime = {
  results: AdhocResult[];
  activeResultId: AdhocResultId | null;
  nextResultIndex: number;
};

export type WorkbenchState = {
  tabs: PersistedAdhocTab[];
  activeTabId: AdhocTabId | null;
  runtimeByTabId: Record<AdhocTabId, AdhocTabRuntime>;
  /**
   * Tabs that have been active at least once, and are therefore mounted.
   *
   * A tab panel must never *first* mount while hidden: `DebouncedFrame` only
   * renders its children once it has measured a non-zero width, so a panel that
   * first mounts inside `display: none` would never show its visualization.
   */
  visitedTabIds: AdhocTabId[];
  nextTabIndex: number;
};

export type NewTabOptions = {
  databaseId?: DatabaseId | null;
  sql?: string;
};
